import {
  Badge,
  Box,
  Divider,
  Heading,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { PatientDTO } from "@metriport/api-sdk";
import { toArray } from "@metriport/shared";
import { useMemo } from "react";
import { PatientFacilityMatches } from "../../../api/patient-matches";

export type isDownloading = Record<
  string,
  {
    type?: string;
    downloading: boolean;
  }
>;

type Source = "user" | "hie";

type ItemWithSource = {
  value: string;
  source: Source;
};

type ContactInfo = {
  phones: ItemWithSource[];
  emails: ItemWithSource[];
  addresses: ItemWithSource[];
};

type JsonAddress = {
  line?: string[];
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

function parseAndFormatAddress(addressStr: string): string {
  const trimmed = addressStr.trim();
  if (!trimmed.startsWith("{")) {
    return trimmed;
  }
  try {
    const parsed: JsonAddress = JSON.parse(trimmed);
    const linePart = parsed.line?.join(", ") ?? "";
    const parts = [linePart, parsed.city, parsed.state, parsed.zip].filter(Boolean);
    return parts.join(", ");
  } catch {
    return trimmed;
  }
}

function extractContactInfoFromPatient(patient: PatientDTO | undefined): ContactInfo {
  if (!patient) {
    return { phones: [], emails: [], addresses: [] };
  }

  const contacts = toArray(patient.contact);
  const phones: ItemWithSource[] = contacts.flatMap(c =>
    c?.phone ? [{ value: c.phone, source: "user" as const }] : []
  );
  const emails: ItemWithSource[] = contacts.flatMap(c =>
    c?.email ? [{ value: c.email, source: "user" as const }] : []
  );

  const addressList = toArray(patient.address);
  const addresses: ItemWithSource[] = addressList.flatMap(addr => {
    if (!addr) return [];
    const parts = [addr.addressLine2, addr.addressLine1, addr.city, addr.state, addr.zip].filter(
      Boolean
    );
    if (parts.length === 0) return [];
    const formatted = parts.join(", ");
    return [{ value: formatted, source: "user" as const }];
  });

  return { phones, emails, addresses };
}

function extractContactInfoFromMatches(matches: PatientFacilityMatches): ContactInfo {
  const allPhones: ItemWithSource[] = [];
  const allEmails: ItemWithSource[] = [];
  const allAddresses: ItemWithSource[] = [];

  for (const match of matches) {
    if (match.patient) {
      for (const phone of match.patient.telephoneNumbers) {
        if (phone) {
          allPhones.push({ value: phone, source: "hie" });
        }
      }
      for (const email of match.patient.emails) {
        if (email) {
          allEmails.push({ value: email, source: "hie" });
        }
      }
      for (const addr of match.patient.addresses) {
        if (addr) {
          allAddresses.push({
            value: parseAndFormatAddress(addr),
            source: "hie",
          });
        }
      }
    }
  }

  return {
    phones: allPhones,
    emails: allEmails,
    addresses: allAddresses,
  };
}

function dedupeItems(items: ItemWithSource[], normalize: (s: string) => string): ItemWithSource[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const normalized = normalize(item.value);
    if (seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
}

// Phones: remove all non-digits
function normalizePhone(s: string): string {
  return s.replace(/[^0-9]/g, "");
}

// Emails: lowercase and trim (preserve @ and .)
function normalizeEmail(s: string): string {
  return s.toLowerCase().trim();
}

// Addresses: lowercase and remove extra whitespace/punctuation
function normalizeAddress(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function mergeContactInfo(patientInfo: ContactInfo, matchesInfo: ContactInfo): ContactInfo {
  return {
    phones: dedupeItems([...patientInfo.phones, ...matchesInfo.phones], normalizePhone),
    emails: dedupeItems([...patientInfo.emails, ...matchesInfo.emails], normalizeEmail),
    addresses: dedupeItems([...patientInfo.addresses, ...matchesInfo.addresses], normalizeAddress),
  };
}

function ContactSection({
  title,
  items,
  emptyMessage,
}: {
  title: string;
  items: ItemWithSource[];
  emptyMessage: string;
}) {
  const userItems = items.filter(i => i.source === "user");
  const hieItems = items.filter(i => i.source === "hie");

  if (items.length === 0) {
    return (
      <Box>
        <Heading size="sm" mb={2}>
          {title}
        </Heading>
        <Text fontSize="md" color="gray.500" pl={2}>
          {emptyMessage}
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="sm" mb={2}>
        {title}
      </Heading>
      <VStack align="start" gap={3} pl={2}>
        {userItems.length > 0 && (
          <Box width="100%">
            <HStack mb={1}>
              <Badge colorScheme="teal" fontSize="xs">
                User Provided
              </Badge>
            </HStack>
            <VStack align="start" gap={1} pl={2}>
              {userItems.map((item, index) => (
                <Text key={`user-${index}`} fontSize="md">
                  • {item.value}
                </Text>
              ))}
            </VStack>
          </Box>
        )}
        {hieItems.length > 0 && (
          <Box width="100%">
            <HStack mb={1}>
              <Badge colorScheme="purple" fontSize="xs">
                From HIE
              </Badge>
            </HStack>
            <VStack align="start" gap={1} pl={2}>
              {hieItems.map((item, index) => (
                <Text key={`hie-${index}`} fontSize="md">
                  • {item.value}
                </Text>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

export function DemographicsModal({
  isOpen,
  patient,
  lastDocQuery,
  patientMatches = [],
  actions,
}: {
  isOpen: boolean;
  patient: PatientDTO | undefined;
  lastDocQuery: string | undefined;
  patientMatches?: PatientFacilityMatches;
  actions: {
    onCloseModal: () => void;
  };
}) {
  const contactInfo = useMemo(() => {
    const patientInfo = extractContactInfoFromPatient(patient);
    const matchesInfo = extractContactInfoFromMatches(patientMatches);
    return mergeContactInfo(patientInfo, matchesInfo);
  }, [patient, patientMatches]);

  const dividerColor = useColorModeValue("gray.300", "gray.600");
  const { phones, emails, addresses } = contactInfo;

  return (
    <Modal isOpen={isOpen} onClose={actions.onCloseModal} size="3xl">
      <ModalOverlay />
      <ModalContent m={5} p={6} borderWidth="1px" borderRadius="lg" shadow="md">
        <ModalCloseButton />
        <Box as="section" p={5}>
          <Text fontSize="2xl" fontWeight="bold" textAlign="center">
            {patient?.firstName} {patient?.lastName}
          </Text>

          <Text mt={2} fontSize="md" textAlign="center">
            {patient?.id}
          </Text>

          {lastDocQuery && (
            <Text mt={2} fontSize="md" textAlign="center" fontStyle="italic">
              Last Refreshed: {lastDocQuery}
            </Text>
          )}

          <Divider borderColor={dividerColor} my={4} />

          <Box display="flex" gap={4} mb={4}>
            <Box flex={1}>
              <Text fontWeight="semibold">Date of Birth:</Text>
              <Text fontSize="md">{patient?.dob}</Text>
            </Box>
            <Box flex={1}>
              <Text fontWeight="semibold">Gender at Birth:</Text>
              <Text fontSize="md">{patient?.genderAtBirth}</Text>
            </Box>
          </Box>

          <Divider borderColor={dividerColor} my={4} />

          <VStack gap={4} align="stretch">
            <ContactSection title="Phone" items={phones} emptyMessage="No phone on file" />
            <Divider borderColor={dividerColor} />
            <ContactSection title="Email" items={emails} emptyMessage="No email on file" />
            <Divider borderColor={dividerColor} />
            <ContactSection title="Address" items={addresses} emptyMessage="No address on file" />
          </VStack>
        </Box>
      </ModalContent>
    </Modal>
  );
}
